import { asMaybeString } from "../../computation-model/data";
import { SerializedComponent } from "../../components/SerializedApp";
import { defined, panic } from "../../Support";
import { makeText } from "../builder-utils";
import {
    ComponentBuilder,
    ComponentConfiguratorContext,
    InteractiveComponentConfiguratorContext,
    RewritingComponentConfiguratorContext,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { makeTextPropertyDescriptor } from "../components/descriptor-utils";
import { PropertySection } from "../components/property-sections";
import { makeStringProperty } from "../components/property-utils";
import { copyClassScreen } from "../copy-screen";
import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    getAllowedFormTables,
    isFormScreen,
    withOutputTable,
    isSingleRelationType,
    areTableNamesEqual,
    getTableRefTableName,
    getTableName,
} from "../description";
import { makeFormScreenDescription } from "../make-screen";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";
import { makeNavigationPropertyDescriptors, navigationTargetModalPropertyHandler } from "./link-to-screen";

export class AlertActionHandler extends BaseActionHandler<ActionDescription> {
    public readonly kind = ActionKind.Alert;
    public readonly iconName = "alert-circle";

    public get needsData(): boolean {
        return true;
    }

    public getDescriptor(): ActionDescriptor {
        return {
            name: "Show Debug Alert",
            properties: [],
        };
    }

    public makeAction(builder: ComponentBuilder): SerializedComponent {
        const { b } = builder;
        return b.actionIfContextDefined(b.action("Alert", { message: b.getContext }));
    }
}

export class EnlargeImageActionHandler extends BaseActionHandler<ActionDescription> {
    public readonly kind = ActionKind.EnlargeImage;
    public readonly iconName = "maximize-2";

    public getDescriptor(): ActionDescriptor {
        return {
            name: "Enlarge image",
            properties: [],
        };
    }

    public makeAction(): SerializedComponent {
        return panic("Enlarge image action must be handled individually");
    }
}

export class UploadImageActionHandler extends BaseActionHandler<ActionDescription> {
    public readonly kind = ActionKind.UploadImage;
    public readonly iconName = "upload";

    public getDescriptor(): ActionDescriptor {
        return {
            name: "Upload image",
            properties: [],
        };
    }

    public makeAction(): SerializedComponent {
        return panic("Upload image action must be handled individually");
    }
}

interface FormScreenActionDescription extends ActionDescription {
    readonly kind: ActionKind.FormScreen;
    readonly title: PropertyDescription;
    readonly formScreenName: string;
}

export class FormScreenActionHandler extends BaseActionHandler<FormScreenActionDescription> {
    public readonly kind = ActionKind.FormScreen;

    public readonly iconName = true;

    public getDescriptor(
        _desc: FormScreenActionDescription | undefined,
        _tables: InputOutputTables | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor {
        return {
            name: "Show Form Screen",
            needsScreenContext: true,
            properties: [
                makeTextPropertyDescriptor("title", "Title", "Form title", true, mutatingScreenKind, {
                    propertySection: PropertySection.Design,
                    searchable: false,
                }),
                ...makeNavigationPropertyDescriptors(ccc, true),
            ],
        };
    }

    public newActionDescription(
        tables: InputOutputTables,
        iccc: InteractiveComponentConfiguratorContext
    ): FormScreenActionDescription | undefined {
        const targetTables = getAllowedFormTables(iccc.schema);
        if (targetTables.length === 0) return undefined;

        const formTable = defined(targetTables.find(t => t !== tables.input) ?? targetTables[0]);

        return {
            kind: this.kind,
            title: makeStringProperty("Form"),
            formScreenName: iccc.addFormScreen(new Set(), () =>
                makeFormScreenDescription(withOutputTable(tables, formTable))
            ),
        };
    }

    public getScreensUsed(desc: FormScreenActionDescription): readonly string[] {
        return [desc.formScreenName];
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: FormScreenActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined
    ): SerializedComponent | undefined {
        const title = makeText(builder, desc.title, tables.input) ?? builder.b.constant("Form");
        return builder.makePushFormScreen(desc.formScreenName, title, defined(screenContext));
    }

    public rewriteAfterReload(
        desc: FormScreenActionDescription,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): FormScreenActionDescription | undefined {
        if (!ccc.requireScreen(desc.formScreenName)) return undefined;

        return super.rewriteAfterReload(desc, tables, ccc, mutatingScreenKind);
    }

    public async duplicateAction(
        desc: FormScreenActionDescription,
        copyTables: InputOutputTables | undefined,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        screensCreated: Set<string>
    ): Promise<FormScreenActionDescription | undefined> {
        if (copyTables === undefined) return undefined;

        const screenDesc = iccc.appDescription.screenDescriptions[desc.formScreenName];
        if (screenDesc === undefined || !isFormScreen(screenDesc)) return undefined;

        if (
            !isSingleRelationType(screenDesc.type) ||
            !areTableNamesEqual(getTableName(copyTables.input), getTableRefTableName(screenDesc.type))
        ) {
            return undefined;
        }

        const screenCopy = await copyClassScreen(screenDesc, iccc, mutatingScreenKind, screensCreated);
        if (screenCopy === undefined) return undefined;

        const newScreenName = iccc.addFormScreen(screensCreated, () => screenCopy);

        return { ...desc, formScreenName: newScreenName };
    }

    public getTokenizedDescription(
        desc: FormScreenActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const titleToken = tokenForProperty(desc.title, tables, ccc);
        if (titleToken === undefined) return undefined;

        return [{ kind: "string", value: "title " }, titleToken];
    }

    public inflate(ib: WireInflationBackend, desc: FormScreenActionDescription): WireActionHydrator | undefined {
        const [titleGetter] = ib.getValueGetterForProperty(desc.title, true);
        const target = navigationTargetModalPropertyHandler.getEnum(desc);

        return vp => {
            const inputRow = vp.rowContext?.inputRow;
            if (inputRow === undefined) return undefined;

            const title = asMaybeString(titleGetter(vp));

            return async ab => {
                ab.pushFormScreen(desc.formScreenName, title, inputRow, target);
                return true;
            };
        };
    }
}
